import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { SunriseCircle } from "../components/icons/SunriseCircle";
import { BRAND, IMAGES, REFLECTIONS } from "../data/content";
import { FadeUp, Stagger, StaggerItem } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

export default function About() {
  return (
    <div data-testid="about-page" className="bg-brand-cream">
      {/* header */}
      <section className="pt-40 md:pt-52 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline">Meet Your Holistic Lifestyle Architect</span></FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-brand-forest tracking-tight leading-[0.95] mt-6 max-w-4xl">
              Rooted in legacy, rising in light.
            </h1>
          </FadeUp>
        </div>
      </section>

      {/* architect */}
      <section className="pb-28 md:pb-40" data-testid="architect">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-14 items-start">
          <div className="md:col-span-6">
            <FadeUp>
              <div className="arch overflow-hidden aspect-[4/5]">
                <img src={IMAGES.architect} alt={BRAND.architect} className="w-full h-full object-cover" />
              </div>
            </FadeUp>
          </div>
          <div className="md:col-span-6 md:pt-10">
            <FadeUp>
              <Quote className="text-brand-gold mb-6" size={40} strokeWidth={1} />
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="font-serif text-3xl md:text-4xl text-brand-forest leading-[1.25] tracking-tight">
                Because a brighter tomorrow isn't something we wait for — it's something we
                intentionally create.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="text-brand-inkSoft text-lg leading-relaxed mt-8">
                As someone who has walked through seasons of uncertainty, carried the weight of
                responsibility, navigated unexpected life transitions, and learned what it means to
                rebuild while still showing up for everyone else, I understand how easy it is to lose
                yourself in the process of caring for others. Those experiences didn't just shape
                me — they called me to this work.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <p className="text-brand-inkSoft text-lg leading-relaxed mt-6">
                Today, as a nurse, birth doula, and Holistic Lifestyle Architect, I help women stop
                surviving and start intentionally architecting lives that truly reflect who they are.
                Together, we cultivate clarity, confidence, healthy habits, emotional well-being, and
                purposeful living — creating transformation that is both meaningful and sustainable.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-10 pt-8 border-t border-brand-beige">
                <span className="font-serif italic text-3xl text-brand-forest">{BRAND.architect}</span>
                <div className="mt-3 space-y-0.5 text-brand-inkSoft text-sm leading-relaxed" data-testid="architect-credentials">
                  <p>LVN, ADN | Home Health &amp; Hospice</p>
                  <p>Certified Postpartum, Labor &amp; Birth Doula</p>
                  <p>HCI Certified Health Coach (HCI-CHC)</p>
                  <p>HCI Certified Life Coach (HCI-CLC)</p>
                </div>
                <p className="overline mt-4">Founder · {BRAND.full}</p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee items={["Clarity", "Confidence", "Grace", "Becoming"]} />

      {/* reflections */}
      <section className="py-28 md:py-40 bg-brand-beige/40" data-testid="reflections">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline">Sound familiar?</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl md:text-6xl text-brand-forest tracking-tight mt-5 max-w-3xl leading-[1.03]">
              What's the story in your head?
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="text-brand-inkSoft text-lg mt-6 max-w-xl">
              If any of these quietly live inside you, you are exactly where you're meant to begin.
            </p>
          </FadeUp>

          <Stagger className="grid md:grid-cols-2 gap-4 mt-14" stagger={0.08}>
            {REFLECTIONS.map((r, i) => (
              <StaggerItem key={i}>
                <div
                  data-testid={`reflection-${i}`}
                  className="group flex items-start gap-5 bg-brand-cream p-7 rounded-2xl hover:bg-brand-forest transition-colors duration-500"
                >
                  <SunriseCircle className="text-brand-gold shrink-0 mt-1 group-hover:text-brand-goldBright transition-colors" size={22} strokeWidth={1.4} />
                  <p className="font-serif text-xl md:text-2xl italic text-brand-forest group-hover:text-brand-cream transition-colors duration-500 leading-snug">
                    "{r}"
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <FadeUp delay={0.2}>
            <div className="mt-16 text-center">
              <p className="font-serif text-2xl md:text-3xl italic text-brand-forest">
                Wanting more doesn't make you ungrateful.
              </p>
              <Link
                to="/contact"
                data-testid="about-cta"
                className="group inline-flex items-center gap-3 mt-10 bg-brand-forest text-brand-cream px-10 py-4 rounded-full text-sm tracking-wide hover:-translate-y-1 hover:shadow-2xl transition-transform duration-300"
              >
                Let's begin
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
